
import { useEffect, useState, type ChangeEventHandler, type DragEventHandler } from "react";
import { IntlShape } from "react-intl";

import { OccurrenceImportResponse, OccurrenceImportValidationResponse } from "../../../../models/occurrenceImport";

import { getOccurrenceImportTemplate, importOccurrenceFile, validateOccurrenceImportFile } from "../../../../api/occurrenceImport";

export const useOccurrenceImport = (intl: IntlShape) => {

    const [occurrenceImportStep, setOccurrenceImportStep] = useState<"fileSelection" | "fileValidation" | "importResult">("fileSelection");
    
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [dowloadingTemplate, setDownloadingTemplate] = useState(false);
    const [isDragActive, setIsDragActive] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [validatingFile, setValidatingFile] = useState(false);
    const canImport = !!selectedFile;

    const [validationResponse, setValidationResponse] = useState<OccurrenceImportValidationResponse | null>(null);
    const [importing, setImporting] = useState(false);

    const [importResponse, setImportResponse] = useState<OccurrenceImportResponse | null>(null);

    useEffect(() => {
        if (!selectedFile) setOccurrenceImportStep("fileSelection");
    }, [selectedFile]);


    const isXlsxFile = (file: File) => {
        const lowerName = file.name.toLowerCase();
        const byExtension = lowerName.endsWith(".xlsx");
        const byMimeType = file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

        return byExtension || byMimeType;
    };

    const handleDroppedFile = (file?: File) => {
        if (!file) return;

        if (!isXlsxFile(file)) {
            setSelectedFile(null);
            setErrorMessage(intl.formatMessage({
                id: "occurrence.import.invalidFile",
                defaultMessage: "Arquivo inválido. Envie um arquivo .xlsx",
            }));
            return;
        }

        setErrorMessage(null);
        setSelectedFile(file);
    };

    const handleDragEnter: DragEventHandler<HTMLDivElement> = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive(true);
    };

    const handleDragOver: DragEventHandler<HTMLDivElement> = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive(true);
    };

    const handleDragLeave: DragEventHandler<HTMLDivElement> = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive(false);
    };

    const handleDrop: DragEventHandler<HTMLDivElement> = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive(false);

        const droppedFile = event.dataTransfer?.files?.[0];
        handleDroppedFile(droppedFile);
    };

    const handleFileInputChange: ChangeEventHandler<HTMLInputElement> = (event) => {
        const pickedFile = event.target.files?.[0];
        handleDroppedFile(pickedFile);
        event.target.value = "";
    };

    const clearSelectedFile = () => {
        setSelectedFile(null);
        setErrorMessage(null);
    };
    
    const handleGetTemplate = async () => {
        setDownloadingTemplate(true);
        try {
            const response = await getOccurrenceImportTemplate(intl);
            setErrorMessage(response?.errorMessage || null);
        } catch (error) {
            setErrorMessage(intl.formatMessage({ id: "occurrence.import.downloadTemplate.errorMessage" }));
        } finally {
            setDownloadingTemplate(false);
        }
    };

    const validateFile = async () => {
        if (!selectedFile) return;

        setValidatingFile(true);
        try {
            const validationResponse = await validateOccurrenceImportFile(selectedFile, intl);
            setValidationResponse(validationResponse);

            setOccurrenceImportStep("fileValidation");
        } catch (error) {
            setErrorMessage(intl.formatMessage({ id: "occurrence.import.validationError" }));
        } finally {
            setValidatingFile(false);
        }

    }

    const handleImport = async () => {
        if (!validationResponse || validationResponse.errors.length > 0 || !selectedFile) return;

        setImporting(true);
        try {
            const response = await importOccurrenceFile(selectedFile, intl);
            setImportResponse(response);
            setOccurrenceImportStep("importResult");
        } catch (error) {
            setErrorMessage(intl.formatMessage({ id: "occurrence.import.importError" }));
        } finally {
            setImporting(false);
        }
    }
    
    return {
        occurrenceImportStep,

        errorMessage,
        dowloadingTemplate,
        isDragActive,
        selectedFile,
        canImport,
        validatingFile,
        handleGetTemplate,
        handleDragEnter,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handleFileInputChange,
        clearSelectedFile,
        validateFile,

        validationResponse,
        importing,
        handleImport,

        importResponse
    }
}